import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DirhamSign from './DirhamSign';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, Send, X, Minimize2, Maximize2, Mic, MicOff, 
  Volume2, VolumeX, User, Bot, TrendingUp, DollarSign,
  Users, CheckSquare, Loader2, ChevronRight, Phone, Mail,
  Calendar, Search, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConversationalAI = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (isOpen && suggestions.length === 0) {
      fetchSuggestions();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/ai/crm/suggestions?token=${token}`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions', error);
    }
  };

  const sendQuery = async (queryText = null) => {
    const query = queryText || input;
    if (!query.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: query,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/crm/query?token=${token}`, {
        query: query
      });

      const result = response.data;
      
      const assistantMessage = {
        role: 'assistant',
        content: result.summary || 'Query processed successfully.',
        data: result,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Query failed', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I couldn't process that query. Try asking about contacts, deals, or tasks.",
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuery();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendQuery(suggestion.query);
  };

  const navigateToResult = (item, type) => {
    if (type === 'contacts') {
      navigate(`/contacts/${item.id}`);
    } else if (type === 'deals') {
      navigate(`/pipeline`);
    }
    setIsOpen(false);
  };

  const renderResultsTable = (data) => {
    if (!data.results || data.results.length === 0) return null;
    
    const results = data.results.slice(0, 5);
    const type = data.query_type;

    return (
      <div className="mt-3 space-y-2">
        {results.map((item, idx) => (
          <div
            key={idx}
            onClick={() => navigateToResult(item, type)}
            className="flex items-center justify-between p-2 bg-white rounded border hover:border-purple-300 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-2">
              {type === 'contacts' && <User size={14} className="text-slate-400" />}
              {type === 'deals' && <DollarSign size={14} className="text-slate-400" />}
              {type === 'tasks' && <CheckSquare size={14} className="text-slate-400" />}
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {item.name || item.title || 'Unknown'}
                </p>
                {type === 'contacts' && item.company && (
                  <p className="text-xs text-slate-500">{item.company}</p>
                )}
                {type === 'deals' && item.value && (
                  <p className="text-xs text-slate-500"><DirhamSign /> {item.value?.toLocaleString()}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {type === 'contacts' && item.lead_score && (
                <Badge className={`text-xs ${item.lead_score >= 80 ? 'bg-red-500' : item.lead_score >= 60 ? 'bg-orange-500' : 'bg-blue-500'} text-white`}>
                  {item.lead_score}
                </Badge>
              )}
              {type === 'deals' && item.stage && (
                <Badge variant="outline" className="text-xs">{item.stage}</Badge>
              )}
              <ChevronRight size={14} className="text-slate-400" />
            </div>
          </div>
        ))}
        {data.count > 5 && (
          <p className="text-xs text-center text-slate-500 pt-2">
            +{data.count - 5} more results
          </p>
        )}
      </div>
    );
  };

  const renderMessage = (msg, idx) => {
    const isUser = msg.role === 'user';
    
    return (
      <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
          {!isUser && (
            <div className="flex items-center gap-1 mb-1">
              <Sparkles size={12} className="text-purple-500" />
              <span className="text-xs text-slate-500">AI Assistant</span>
            </div>
          )}
          <div className={`rounded-lg px-3 py-2 ${
            isUser 
              ? 'bg-purple-600 text-white' 
              : 'bg-slate-100 text-slate-800'
          }`}>
            <p className="text-sm">{msg.content}</p>
          </div>
          {msg.data && msg.data.results && (
            renderResultsTable(msg.data)
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform duration-200 z-50"
        data-testid="ai-assistant-button"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  return (
    <Card 
      className={`fixed shadow-2xl z-50 flex flex-col border-purple-200 border-2 transition-all duration-300 ${
        isExpanded 
          ? 'bottom-4 right-4 w-[600px] h-[600px]' 
          : 'bottom-6 right-6 w-96 h-[500px]'
      }`}
      data-testid="conversational-ai-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <div>
            <h3 className="font-semibold text-sm">AI Assistant</h3>
            <p className="text-xs text-purple-200">Ask me anything about your CRM</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            className="hover:bg-white/20 p-1.5 rounded"
            title={voiceEnabled ? "Disable voice" : "Enable voice"}
          >
            {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="hover:bg-white/20 p-1.5 rounded"
          >
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="hover:bg-white/20 p-1.5 rounded"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 bg-white">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col justify-center items-center text-center px-4">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <Search size={28} className="text-purple-600" />
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Ask me anything!</h4>
            <p className="text-sm text-slate-500 mb-4">
              Try natural language queries about your contacts, deals, and tasks.
            </p>
            
            {/* Suggestions */}
            <div className="w-full space-y-2">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Try asking:</p>
              {suggestions.slice(0, 4).map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(s)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50 transition-colors text-sm text-slate-700 flex items-center gap-2"
                >
                  <Zap size={14} className="text-purple-500" />
                  {s.query}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => renderMessage(msg, idx))}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-slate-100 rounded-lg px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Quick Actions */}
      {messages.length > 0 && (
        <div className="px-3 py-2 border-t bg-slate-50 flex gap-2 overflow-x-auto">
          {suggestions.slice(0, 3).map((s, idx) => (
            <button
              key={idx}
              onClick={() => handleSuggestionClick(s)}
              className="flex-shrink-0 text-xs px-2 py-1 rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50"
            >
              {s.query}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t bg-white rounded-b-lg">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about contacts, deals, tasks..."
            disabled={loading}
            className="flex-1 text-sm"
            data-testid="crm-query-input"
          />
          <Button
            onClick={() => sendQuery()}
            disabled={loading || !input.trim()}
            className="bg-purple-600 hover:bg-purple-700"
            size="sm"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ConversationalAI;
